import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { CalendarIcon, LightbulbIcon, PlusIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H1 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Placeholder,
  PlaceholderImage,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { AnonymousChip, ProfileChip } from "components/lib/Chips";
import {
  EntityBannerMedia,
  EntityCover,
  EntityHeaderActions,
  EntityHeaderContent,
  EntityHeaderMain,
  EntityHeaderMeta,
  EntityHeaderOverlap,
  EntityHeaderTagline,
  EntityHeaderTitles,
  HeaderActions,
  HeaderContainer,
  HeaderInfo,
  StyledContainer,
} from "components/lib/EntityHeader";
import Tag, { TagContainer } from "components/lib/Tag";

import type {
  ChallengeHeader as ChallengeHeaderType,
  ChallengeIdeaListItem,
} from "../types";
import InnoIntelSearchAction from "./InnoIntelSearchAction";

// Re-export for existing wizard / group imports
export { HeaderActions, HeaderContainer, HeaderInfo, StyledContainer };

type ChallengeHeaderProps = {
  challenge?: ChallengeHeaderType;
  myIdeas: ChallengeIdeaListItem[];
  newIdea: () => void;
  isPreview?: boolean;
  redirectToLogin: () => void;
  loading: boolean;
};

const ChallengeHeader = ({
  challenge,
  myIdeas,
  newIdea,
  isPreview = false,
  redirectToLogin,
  loading,
}: ChallengeHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const canMakeIdea = util.canCreateIdea(user, challenge);

  const myUnsubmittedIdeas = useMemo(
    () => myIdeas?.filter((idea) => !idea.isSubmitted),
    [myIdeas],
  );

  const bannerImage = challenge
    ? util.getThumbnail(challenge, "bannerImage", "2560x400")
    : "";
  const coverImage = challenge?.coverImageUrl
    ? util.getThumbnail(challenge, "coverImage", "500x500")
    : null;

  return (
    <EntityHeaderContent
      style={
        isPreview ? { paddingLeft: "2rem", paddingRight: "2rem" } : undefined
      }
      fluid
    >
      {loading ? (
        <Placeholder>
          <PlaceholderImage square style={{ width: 168, height: 168 }} />
          <PlaceholderParagraph>
            <PlaceholderLine length="medium" style={{ height: 50 }} />
            <PlaceholderLine length="short" style={{ height: 30 }} />
            <PlaceholderLine length="short" style={{ height: 20 }} />
          </PlaceholderParagraph>
        </Placeholder>
      ) : challenge ? (
        <div>
          <EntityBannerMedia
            image={bannerImage || undefined}
            height={theme.sizes.isMobile ? 120 : 200}
          />
          <EntityHeaderOverlap
            style={bannerImage ? undefined : { marginTop: 0 }}
          >
            <EntityCover image={coverImage} />
            <EntityHeaderMain
              style={{
                ...(!bannerImage || theme.sizes.isMobile
                  ? { paddingTop: 0 }
                  : undefined),
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <EntityHeaderTitles>
                {challenge.ownerTags?.length ? (
                  <TagContainer style={{ marginBottom: 4 }}>
                    {challenge.ownerTags.map(
                      (
                        tag: NonNullable<
                          ChallengeHeaderType["ownerTags"]
                        >[number],
                      ) => (
                        <Tag key={tag._id} tag={tag} />
                      ),
                    )}
                  </TagContainer>
                ) : null}
                <H1>{challenge?.name}</H1>
                {challenge.tagline ? (
                  <EntityHeaderTagline>{challenge.tagline}</EntityHeaderTagline>
                ) : null}
                <EntityHeaderMeta>
                  {challenge.ownerOrganisation ? (
                    <span>
                      {t("challenge.managedBy")}{" "}
                      <strong>{challenge.ownerOrganisation.name}</strong>
                    </span>
                  ) : null}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {t("challenge.postedBy")}{" "}
                    {challenge.postedByType === "anonymous" ? (
                      <AnonymousChip />
                    ) : null}
                    {challenge.postedByType === "organisation" ||
                    challenge.postedByType === "profile" ? (
                      <ProfileChip
                        compact="very"
                        profile={challenge.postedBy}
                        forType={challenge.postedByType}
                      />
                    ) : null}
                  </span>
                  {challenge.deadline ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Icon icon={CalendarIcon} />
                      {t("challenge.deadline")}{" "}
                      {formatDate(challenge.deadline, "HH:mm")},{" "}
                      {formatDate(challenge.deadline, "dd/MM/yyyy")}
                    </span>
                  ) : null}
                  {!user && (
                    <>
                      {!challenge.stage && (
                        <Label color="yellow">
                          {t("challenge.stages.draft.title")}
                        </Label>
                      )}
                      {challenge.stage === "published" && (
                        <Label color="green">
                          {t("challenge.stages.published.title")}
                        </Label>
                      )}
                      {challenge.stage === "closed" && (
                        <Label color="red">{t("generic.closed")}</Label>
                      )}
                    </>
                  )}
                </EntityHeaderMeta>
              </EntityHeaderTitles>
              <EntityHeaderActions
                className={theme.sizes.isMobile ? "is-mobile" : undefined}
                style={{ justifyContent: "flex-end", width: "100%" }}
              >
                <InnoIntelSearchAction challenge={challenge} />
                {(canMakeIdea || !user) &&
                (challenge.stage === "published" ||
                  util.canManageChallenge(user, challenge) ||
                  util.hasPermission(
                    user,
                    "challenge.editSettings",
                    challenge?._id,
                  )) ? (
                  <Button
                    size={theme.sizes.isMobile ? "small" : undefined}
                    variant={
                      myUnsubmittedIdeas?.length === 0 ? undefined : "outline"
                    }
                    onClick={canMakeIdea ? newIdea : () => redirectToLogin()}
                    color={
                      myUnsubmittedIdeas?.length === 0 ? "primary" : undefined
                    }
                  >
                    <Icon icon={PlusIcon} />
                    {challenge?.buttonText
                      ? challenge.buttonText
                      : t("challenge.ideas.createYourIdea")}
                  </Button>
                ) : null}
                {user && myUnsubmittedIdeas?.length > 0 && (
                  <React.Fragment>
                    {myUnsubmittedIdeas?.length === 1 && (
                      <ButtonLink
                        size={theme.sizes.isMobile ? "small" : undefined}
                        color="primary"
                        to={`/ideas/${myUnsubmittedIdeas[0]?._id}`}
                      >
                        <Icon icon={LightbulbIcon} />
                        {t("challenge.continueIdea")}
                      </ButtonLink>
                    )}
                    {myUnsubmittedIdeas?.length > 1 && (
                      <Dropdown
                        direction="left"
                        upward
                        trigger={
                          <Button
                            size={theme.sizes.isMobile ? "small" : undefined}
                            color="primary"
                          >
                            <Icon icon={LightbulbIcon} />
                            {t("challenge.continueIdea")}
                          </Button>
                        }
                      >
                        <DropdownMenu>
                          {myUnsubmittedIdeas.map((i) => (
                            <DropdownItemLink
                              key={i._id}
                              to={`/ideas/${i._id}`}
                            >
                              {i.name}
                            </DropdownItemLink>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    )}
                  </React.Fragment>
                )}
              </EntityHeaderActions>
            </EntityHeaderMain>
          </EntityHeaderOverlap>
        </div>
      ) : null}
    </EntityHeaderContent>
  );
};

export default ChallengeHeader;
